import { useContext, useState, useCallback, useEffect } from 'react';

import { Form, Formik, FormikErrors } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import { Autocomplete, Button, Card, Divider, List, ListItemButton, Stack, styled, Typography } from '@mui/material';

import { SubmitButton } from '../../../components/buttons/SubmitButton';
import { TextField, BrowseUpload } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults, ErrorMessages } from '../../../constants';
import { useGetCategoriesByPage } from '../../../lib/category/hooks';
import { useGetSubCategory } from '../../../lib/category/hooks/useGetSubCategory';
import useMediaUpload from '../../../lib/media/hooks/useMediaUpload';
import { useUpdateSubCategory } from '../../../lib/sub-category/hooks';
import { checkFileSize } from '../../../lib/util/helpers';
import { Category } from '../../../types/Category';
import { Media } from '../../../types/Media';

import AddParentCategoyButton from './AddParentCategoyButton';
import RemoveParentCategoryButton from './RemoveParentCategoryButton';

function SubCategoryEdit() {
  const navigate = useNavigate();
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const { id = '' } = useParams();
  const [searchCategory, setSearchCategory] = useState('');
  const [photosErrorMsg, setPhotosErrorMsg] = useState<string>('');
  const debouncedSearchCategoryFn = useDebouncedCallback((value: string) => {
    setSearchCategory(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const { data: subCategory, isLoading: isLoadingSubCategory } = useGetSubCategory(id);

  const { data: categories } = useGetCategoriesByPage({
    page: 0,
    sort: '',
    search: searchCategory,
  });

  const { mutateAsync: updateSubCategory, isLoading: isLoadingUpdate } = useUpdateSubCategory();

  const {
    upload,
    error: uploadError,
    isUploading,
  } = useMediaUpload({
    onError: () => {
      setAlertSnackbar({
        message: ErrorMessages.SERVER_ERROR,
        severity: 'error',
      });
      setPhotosErrorMsg(ErrorMessages.SERVER_ERROR);
    },
  });

  useEffect(() => {
    if (!uploadError) return;
    setAlertSnackbar({
      message: ErrorMessages.SERVER_ERROR,
      severity: 'error',
    });
    setPhotosErrorMsg(ErrorMessages.SERVER_ERROR);
  }, [setAlertSnackbar, uploadError]);

  const handlePhotoUpload = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<SubCategoryFormType>>,
      name: string,
    ) => {
      const files = event.target.files;

      if (files && files[0]) {
        const file = files[0];
        try {
          checkFileSize(file);
          setPhotosErrorMsg('');

          const uploadedMedia = await upload(file);

          await setFieldValue(name, uploadedMedia);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : ErrorMessages.SERVER_ERROR;
          setPhotosErrorMsg(errorMessage);
          setAlertSnackbar({
            message: errorMessage,
            severity: 'error',
          });
        }
      }
    },
    [upload, setAlertSnackbar],
  );

  const handleSubmit = async (values: SubCategoryFormType) => {
    await updateSubCategory(
      {
        subCategoryId: id,
        params: {
          title: values.title,
          media: values.media,
          published: !!subCategory?.published,
          category: {
            id: values.categoryId || '',
          },
        },
      },
      {
        onSuccess: () => {
          setAlertSnackbar({
            message: 'Sub Category updated',
            severity: 'success',
            vertical: 'top',
          });
          navigate('/cms/sub-categories');
        },
      },
    );
  };

  return (
    <Container>
      <TopBar>
        <Typography variant="h5">Sub Category</Typography>
      </TopBar>

      {isLoadingSubCategory ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={{
            categoryId: subCategory?.parent.id,
            title: subCategory?.title,
            media: subCategory?.media,
          }}
          validationSchema={SubCategoryEditSchema}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
            <StyledCard>
              <Form>
                <Stack spacing={2} width="100%">
                  <BrowseUpload
                    fileLabel="Upload Photo"
                    name="media"
                    handleFileInput={(event) => handlePhotoUpload(event, setFieldValue, 'media')}
                    handleBlur={handleBlur}
                    disabled={isUploading}
                    isUploading={isUploading}
                    errorMsg={photosErrorMsg}
                    helperText={touched.media && (errors.media as string)}
                    error={touched.media && !!errors.media}
                  />

                  {values.media ? (
                    <>
                      <Typography>Uploaded Photo</Typography>
                      <img src={values.media?.smallUrl} alt="" style={{ maxWidth: '240px', width: '100%', height: 'auto' }} />
                    </>
                  ) : null}

                  <Autocomplete<Category>
                    disablePortal
                    options={categories ?? []}
                    getOptionLabel={(option) => option.title}
                    value={categories?.find((c) => c.id === values.categoryId) || null}
                    onChange={(_, value) => {
                      setFieldValue('categoryId', value ? value.id : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={touched.categoryId && !!errors.categoryId}
                        type="text"
                        name="categoryId"
                        label="Category"
                        onChange={(e) => {
                          handleChange(e);
                          debouncedSearchCategoryFn(e.target.value);
                        }}
                        onBlur={handleBlur}
                        value={values.categoryId}
                        helperText={touched.categoryId && errors.categoryId}
                      />
                    )}
                  />

                  <TextField
                    error={touched.title && !!errors.title}
                    type="text"
                    name="title"
                    label="Name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                    helperText={touched.title && errors.title}
                  />
                </Stack>

                <Divider sx={{ mt: 6 }} />

                <Stack>
                  {/* Additional Parent Categories */}
                  <Stack flexDirection="row" alignItems="center" justifyContent="space-between" spacing={1} marginTop={2} marginBottom={1}>
                    <Typography variant="h6" marginTop="20px" marginBottom="10px">
                      Additional Parent Categories
                    </Typography>

                    <AddParentCategoyButton subCategoryId={subCategory?.id || ''} />
                  </Stack>

                  {subCategory?.additionalParents?.length ? (
                    <List>
                      {subCategory?.additionalParents.map((parent) => (
                        <>
                          <ListItemButton key={parent.id}>
                            <Stack flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
                              <Typography>{parent.title}</Typography>
                              <RemoveParentCategoryButton parentCategoryId={parent.id} subCategoryId={subCategory?.id || ''} />
                            </Stack>
                          </ListItemButton>
                          <Divider />
                        </>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2">No additional parent categories</Typography>
                  )}
                </Stack>

                <Divider sx={{ mt: 6 }} />

                <ButtonContainer>
                  <Buttons>
                    <SubmitButton type="submit" disabled={isLoadingUpdate || isUploading} loading={isLoadingUpdate} variant="contained">
                      Save
                    </SubmitButton>
                    <Button onClick={() => navigate(-1)} type="button">
                      Cancel
                    </Button>
                  </Buttons>
                </ButtonContainer>
              </Form>
            </StyledCard>
          )}
        </Formik>
      )}
    </Container>
  );
}

export default SubCategoryEdit;

type SubCategoryFormType = {
  title?: string;
  categoryId?: string;
  media?: Media;
};

const SubCategoryEditSchema = Yup.object().shape({
  categoryId: Yup.string().required('required'),
  title: Yup.string().required('required'),
  media: Yup.mixed().nullable(),
});

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  padding: '40px',
  marginBottom: '32px',
});

const ButtonContainer = styled('div')({
  marginTop: '20px',
  alignSelf: 'stretch',
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',

  '& > *': {
    margin: '5px',
  },
});
